import { type CountryCode } from "@wayflyer/flyui";
import { defineMessage } from "react-intl";

type TranslationContext = { id: string; defaultMessage: string; description?: string };
type LinkContext = { linkText: TranslationContext; url: string; external: boolean };

type SummaryListHeaderKeys = "yourApplication";
type SummaryListLabelKeys = "applicationAmount" | "applicationExpiry";

type SummaryList = {
  headers: Record<SummaryListHeaderKeys, TranslationContext>;
  labels: Record<SummaryListLabelKeys, TranslationContext>;
};

type Faq = {
  question: TranslationContext;
  answer: TranslationContext;
};

type FaqKey = "beneficialOwner" | "contractSigner" | "verificationInfo" | "bankingDisclosure";

type SupportingLinkHeaderKey = "needHelp";
type SupportingLinkKey = "verifyYourBusiness" | "helpCentre";

type SupportingLink = {
  headers: Record<SupportingLinkHeaderKey, TranslationContext>;
  links: Record<SupportingLinkKey, LinkContext>;
};

type SecurityInfoLabelKey = "dataSecured";
type SecurityInfoLinkKey = "privacyPolicy";
type SecurityInfo = {
  labels: Record<SecurityInfoLabelKey, TranslationContext>;
  links: Record<SecurityInfoLinkKey, LinkContext>;
};

interface TranslatedSupportingContent {
  summaryList: SummaryList;
  faqs: Record<FaqKey, Faq>;
  supportingLinks: SupportingLink;
  securityInfo: SecurityInfo;
}

export const getSupportingContent = ({ country }: { country: CountryCode }): TranslatedSupportingContent => ({
  summaryList: {
    headers: {
      yourApplication: defineMessage({
        id: "shared-ui: summary list header your application",
        defaultMessage: "Your application",
      }),
    },
    labels: {
      applicationAmount: defineMessage({
        id: "shared-ui: summary list application amount",
        defaultMessage: "Amount",
      }),
      applicationExpiry: defineMessage({
        id: "shared-ui: summary list application expiry",
        defaultMessage: "Expires",
      }),
    },
  },
  faqs: {
    bankingDisclosure: {
      question: defineMessage({
        id: "shared-ui: banking disclosure faq question",
        defaultMessage: "Banking",
      }),
      answer: defineMessage({
        id: "shared-ui: banking disclosure faq answer",
        defaultMessage:
          "Wayflyer is a financial technology company and is not a bank. Banking services provided by Thread Bank, Member FDIC.",
      }),
    },
    verificationInfo: {
      question: defineMessage({
        id: "shared-ui: verification info faq question",
        defaultMessage: "Why do we need this information?",
      }),
      answer: defineMessage({
        id: "shared-ui: verification info faq answer",
        defaultMessage:
          "We need to verify your business so we can comply with anti-money laundering regulations and meet our legal obligations to prevent financial crime. ",
      }),
    },
    beneficialOwner:
      country === "DE"
        ? {
            question: defineMessage({
              id: "shared-ui: beneficial owner faq question",
              defaultMessage: "Who is considered a beneficial owner?",
            }),
            answer: defineMessage({
              id: "shared-ui: beneficial owner germany faq answer",
              defaultMessage:
                "A beneficial owner is an individual who owns more than 25% of the equity interests or voting rights of the business.",
            }),
          }
        : {
            question: defineMessage({
              id: "shared-ui: beneficial owner faq question",
              defaultMessage: "Who is considered a beneficial owner?",
            }),
            answer: defineMessage({
              id: "shared-ui: beneficial owner faq answer",
              defaultMessage:
                "A beneficial owner is an individual who owns 25% or more of the equity interests or voting rights of the business.",
            }),
          },
    contractSigner: {
      question: defineMessage({
        id: "shared-ui: contract signer faq question",
        defaultMessage: "Who is allowed to sign the contract?",
      }),
      answer: defineMessage({
        id: "shared-ui: contract signer faq answer",
        defaultMessage:
          "The contract must be signed by a director or senior executive who has the authority to sign documents on behalf of the business.",
      }),
    },
  },
  supportingLinks: {
    headers: {
      needHelp: defineMessage({
        id: "shared-ui: supporting links need help header",
        defaultMessage: "Need help?",
      }),
    },
    links: {
      verifyYourBusiness: {
        linkText: defineMessage({
          id: "shared-ui: supporting link verify your business",
          defaultMessage: "Learn more about verifying your business",
        }),
        url: "https://wayflyer.com/help-center/business-verification",
        external: true,
      },
      helpCentre: {
        linkText: defineMessage({
          id: "shared-ui: supporting link help centre",
          defaultMessage: "Learn more in our Help Center",
        }),
        url: "https://help-center.wayflyer.com/en/",
        external: true,
      },
    },
  },
  securityInfo: {
    labels: {
      dataSecured: defineMessage({
        id: "shared-ui: data security statement",
        defaultMessage: "Your data is securely stored in encrypted databases.",
      }),
    },
    links: {
      privacyPolicy: {
        linkText: defineMessage({
          id: "shared-ui: supporting link privacy notice",
          defaultMessage: "View our Privacy notice",
        }),
        url: "https://www.wayflyer.com/privacy-notice",
        external: true,
      },
    },
  },
});
